import { useMemo, useCallback } from "react";
import { normalizeValue, selectionToWorkflowEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";
import { useWorkflowPool } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type WorkflowSelectorProps = Omittable<{
  value?: string | string[] | null;
  onValueChange?: (ids: string[]) => void;
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  chips?: boolean;
  icon?: IconName;
  fill?: boolean;
  contentFit?: boolean;
  contentSize?: EntitySelectorContentSize;
  /** When true, only workflows the viewer can edit are listed. Use
   *  for create-issue / move-issue dialogs. */
  editableOnly?: boolean;
  children?: React.ReactNode;
}>;

export function WorkflowSelector({
  value,
  onValueChange,
  maxSelections = 1,
  variant,
  placeholder = "Select workflow...",
  size,
  disabled,
  chips,
  icon,
  fill,
  contentFit,
  contentSize,
  editableOnly = false,
  children,
}: WorkflowSelectorProps) {
  const { pool } = useWorkflowPool({ editableOnly });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections,
      searchPlaceholder: "Search workflows...",
      emptyMessage: "No workflows found",
      noItemsMessage: "No workflows yet",
    }),
    [pool, maxSelections],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const ids = normalizeValue(value);
    if (ids.length === 0) return [];
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return ids
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: pool.id, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onValueChange={handleValueChange}>
      {children ?? (
        <>
          {chips ? (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
              chips
              fill={fill}
              icon={icon}
            />
          ) : (
            <SelectionEntityTrigger
              toEntity={selectionToWorkflowEntity}
              placeholder={placeholder}
              title="Workflow"
              emptyIcon={icon}
              size={size}
              disabled={disabled}
              fill={fill}
            />
          )}
          <EntitySelectorContent size={contentSize} fit={contentFit}>
            <EntitySelectorInput />
            <EntitySelectorPool value={pool.id} />
            <EntitySelectorEmpty>No workflows found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
